import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Filter, Download , ChevronUp, ChevronDown} from "lucide-react";
import { PatternFormat } from "react-number-format";

export interface PatientFiltersData {
  nameLike?: string;
  cpf?: string;
  birthDate?: string;
  email?: string;
  cellPhoneLike?: string;
  pageSize?: number;
}

interface PatientFiltersProps {
  onFilterChange: (filters: PatientFiltersData) => void;
  includeInactive: boolean;
  onIncludeInactiveChange: (value: boolean) => void;
}

export function PatientFilters({
  onFilterChange,
  includeInactive,
  onIncludeInactiveChange,
}: PatientFiltersProps) {
  const [showFilters, setShowFilters] = useState(true);
  const [nameLike, setNameLike] = useState<string>("");
  const [cpf, setCpf] = useState<string>("");
  const [birthDate, setBirthDate] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [cellPhoneLike, setCellPhoneLike] = useState<string>("");
  const [pageSize, setPageSize] = useState<string>("20");

  const handleFilter = () => {
    onFilterChange({
      nameLike: nameLike || undefined,
      cpf: cpf || undefined,
      birthDate: birthDate || undefined,
      email: email || undefined,
      cellPhoneLike: cellPhoneLike || undefined,
      pageSize: parseInt(pageSize),
    });
  };

  const handleClearFilters = () => {
    setNameLike("");
    setCpf("");
    setBirthDate("");
    setEmail("");
    setCellPhoneLike("");
    setPageSize("20");
    onFilterChange({});
  };

  return (
    <div className="space-y-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowFilters(!showFilters)}
        className="filter-toggle"
      >
        {showFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {showFilters ? "Esconder Filtros" : "Mostrar Filtros"}
      </Button>

      {showFilters && (
        <div className="filter-box">
          {/* Linha 1: Nome, CPF, Data de nascimento e Registros por página */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div className="space-y-1">
              <Label htmlFor="name" className="filter-label">Nome (parcial)</Label>
              <Input
                id="name"
                placeholder="Digite parte do nome"
                className="filter-input"
                value={nameLike}
                onChange={(e) => setNameLike(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="cpf" className="filter-label">CPF</Label>
              <PatternFormat
                id="cpf"
                format="###.###.###-##"
                mask="_"
                customInput={Input}
                value={cpf}
                onValueChange={(values) => setCpf(values.value)}
                placeholder="000.000.000-00"
                className="filter-input"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="birthDate" className="filter-label">Data de Nascimento</Label>
              <Input
                id="birthDate"
                type="date"
                className="filter-input"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="pageSize" className="filter-label">Registros por Página</Label>
              <Select value={pageSize} onValueChange={setPageSize}>
                <SelectTrigger id="pageSize" className="filter-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover z-50">
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Linha 2: Email, Telefone, checkbox e botões */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div className="space-y-1">
              <Label htmlFor="email" className="filter-label">Email</Label>
              <Input
                id="email"
                placeholder="Digite o email"
                className="filter-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="cellPhone" className="filter-label">Telefone (parcial)</Label>
              <Input
                id="cellPhone"
                placeholder="Digite parte do telefone"
                className="filter-input"
                value={cellPhoneLike}
                onChange={(e) => setCellPhoneLike(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="includeInactive"
                checked={includeInactive}
                onCheckedChange={onIncludeInactiveChange}
              />
              <Label htmlFor="includeInactive" className="text-xs cursor-pointer">
                Incluir inativos
              </Label>
            </div>

            <div className="flex gap-2">
              <Button size="sm" onClick={handleFilter} className="flex-1 gap-1.5 h-7 text-xs">
                <Search className="h-3 w-3" /> Filtrar
              </Button>
              <Button size="sm" variant="outline" onClick={handleClearFilters} className="flex-1 gap-1.5 h-7 text-xs">
                Limpar
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs">
                <Download className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}